/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Users, TrendingUp, AlertTriangle, UserCheck, UserMinus, BarChart3,
  Download, Activity, Shield, ArrowUpRight, ArrowDownRight,
  FileText, Calendar, MessageSquare, ChevronRight, Zap, Eye, Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LeadershipBadge } from '../../components/LeadershipBadge';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH_COLORS = {
  active: { bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50', label: 'Active' },
  low_engagement: { bg: 'bg-amber-400', text: 'text-amber-700', light: 'bg-amber-50', label: 'Low Engagement' },
  at_risk: { bg: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-50', label: 'At Risk' },
  inactive: { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-50', label: 'Inactive' },
};

const QUALITY_COLORS = {
  excellent: { bg: 'bg-emerald-500', label: 'Excellent (80%+)' },
  good: { bg: 'bg-blue-500', label: 'Good (50-79%)' },
  needs_work: { bg: 'bg-amber-400', label: 'Needs Work (25-49%)' },
  incomplete: { bg: 'bg-red-400', label: 'Incomplete (<25%)' },
};

const BoardDashboardPage = () => {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [dashRes, membersRes] = await Promise.all([
        axios.get(`${API}/admin/intelligence/dashboard`, { withCredentials: true }),
        axios.get(`${API}/admin/intelligence/members`, { withCredentials: true }),
      ]);
      setData(dashRes.data);
      setMembers(membersRes.data);
    } catch { /* */ } finally { setLoading(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/intelligence/export`, { withCredentials: true });
      const rows = res.data;
      if (!rows.length) return;
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `dccf-members-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch { /* */ } finally { setExporting(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-2 border-[#002249] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return null;
  const k = data.kpis;
  const healthTotal = Object.values(data.health_distribution).reduce((a, b) => a + b, 0) || 1;
  const qualityTotal = Object.values(data.profile_quality).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="p-5 lg:p-8 max-w-7xl mx-auto" data-testid="board-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#002249]" data-testid="board-dashboard-title">Executive Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Chamber intelligence & member analytics</p>
        </div>
        <Button onClick={handleExport} disabled={exporting} variant="outline" className="rounded-sm border-[#002249] text-[#002249] hover:bg-[#002249] hover:text-white" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" />{exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>

      {/* Insights Bar */}
      {data.insights.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="insights-panel">
          {data.insights.map((insight, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
              className={`flex items-start gap-3 px-4 py-3 rounded-sm text-sm ${
                insight.type === 'warning' ? 'bg-amber-50 border border-amber-200 text-amber-800' :
                insight.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' :
                'bg-blue-50 border border-blue-200 text-blue-800'
              }`} data-testid={`insight-${i}`}>
              {insight.type === 'warning' ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
               insight.type === 'success' ? <TrendingUp className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
               <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <span>{insight.text}</span>
            </motion.div>
          ))}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="kpi-cards">
        {[
          { label: 'Total Members', value: k.total_members, icon: Users, color: '#002249' },
          { label: 'Active Members', value: k.active_members, icon: UserCheck, color: '#10B981', sub: `${Math.round((k.active_members / Math.max(k.total_members, 1)) * 100)}% of total` },
          { label: 'At Risk', value: k.at_risk_members, icon: AlertTriangle, color: '#F59E0B', sub: k.at_risk_members > 0 ? 'Need attention' : 'All clear' },
          { label: 'Inactive', value: k.inactive_members, icon: UserMinus, color: '#EF4444', sub: k.inactive_members > 0 ? '60+ days no activity' : 'None' },
          { label: 'Engagement Rate', value: `${k.engagement_rate}%`, icon: Activity, color: k.engagement_rate >= 30 ? '#10B981' : '#F59E0B' },
          { label: 'Posts This Month', value: k.posts_this_month, icon: MessageSquare, color: '#6366F1' },
          { label: 'New This Month', value: k.new_members_month, icon: ArrowUpRight, color: '#10B981' },
          { label: 'Pending Apps', value: k.pending_applications, icon: FileText, color: '#8B5CF6' },
        ].map((card, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="bg-white border border-slate-200 rounded-sm p-4 sm:p-5" data-testid={`kpi-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${card.color}12` }}>
                <card.icon className="w-4.5 h-4.5" style={{ color: card.color }} />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-[#002249]">{card.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{card.label}</p>
            {card.sub && <p className="text-[10px] text-slate-400 mt-1">{card.sub}</p>}
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto" data-testid="dashboard-tabs">
        {[
          { id: 'overview', label: 'Health & Quality', icon: Eye },
          { id: 'performers', label: 'Top Performers', icon: TrendingUp },
          { id: 'members', label: 'All Members', icon: Users },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'border-[#002249] text-[#002249]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`} data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* TAB: Health & Quality */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Health Distribution */}
          <div className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6" data-testid="health-chart">
            <h3 className="text-base font-bold text-[#002249] mb-4 flex items-center gap-2"><Activity className="w-4 h-4" /> Member Health</h3>
            <div className="flex h-6 rounded-full overflow-hidden mb-4">
              {Object.entries(data.health_distribution).map(([key, val]) => (
                val > 0 && <div key={key} className={`${HEALTH_COLORS[key].bg} transition-all`} style={{ width: `${(val / healthTotal) * 100}%` }} title={`${HEALTH_COLORS[key].label}: ${val}`} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(data.health_distribution).map(([key, val]) => (
                <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-sm ${HEALTH_COLORS[key].light}`}>
                  <div className={`w-3 h-3 rounded-full ${HEALTH_COLORS[key].bg}`} />
                  <div>
                    <p className={`text-xs font-bold ${HEALTH_COLORS[key].text}`}>{val}</p>
                    <p className="text-[10px] text-slate-500">{HEALTH_COLORS[key].label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Profile Quality */}
          <div className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6" data-testid="quality-chart">
            <h3 className="text-base font-bold text-[#002249] mb-4 flex items-center gap-2"><Target className="w-4 h-4" /> Profile Quality</h3>
            <div className="flex h-6 rounded-full overflow-hidden mb-4">
              {Object.entries(data.profile_quality).map(([key, val]) => (
                val > 0 && <div key={key} className={`${QUALITY_COLORS[key].bg} transition-all`} style={{ width: `${(val / qualityTotal) * 100}%` }} title={`${QUALITY_COLORS[key].label}: ${val}`} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(data.profile_quality).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2 px-3 py-2 rounded-sm bg-slate-50">
                  <div className={`w-3 h-3 rounded-full ${QUALITY_COLORS[key].bg}`} />
                  <div>
                    <p className="text-xs font-bold text-slate-700">{val}</p>
                    <p className="text-[10px] text-slate-500">{QUALITY_COLORS[key].label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Industry Distribution */}
          {data.industry_distribution.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6 lg:col-span-2" data-testid="industry-chart">
              <h3 className="text-base font-bold text-[#002249] mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Industries Represented</h3>
              <div className="space-y-2">
                {data.industry_distribution.map((ind, i) => {
                  const maxCount = data.industry_distribution[0]?.count || 1;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-slate-600 w-36 truncate capitalize">{ind.industry.replace('_', ' ')}</span>
                      <div className="flex-1 h-5 bg-slate-100 rounded-sm overflow-hidden">
                        <div className="h-full bg-[#002249] rounded-sm transition-all" style={{ width: `${(ind.count / maxCount) * 100}%` }} />
                      </div>
                      <span className="text-xs font-bold text-[#002249] w-8 text-right">{ind.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Top Performers */}
      {activeTab === 'performers' && (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="top-performers">
          <div className="p-5 border-b border-slate-100">
            <h3 className="text-base font-bold text-[#002249] flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Top Contributors</h3>
            <p className="text-xs text-slate-500 mt-1">Ranked by community engagement (posts, comments, events)</p>
          </div>
          {members && members.slice(0, 15).map((m, i) => (
            <div key={m.id} className={`flex items-center gap-4 px-5 py-3 ${i > 0 ? 'border-t border-slate-50' : ''} hover:bg-slate-50/50 transition-colors`} data-testid={`performer-${i}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-[#002249] text-white' : 'bg-slate-100 text-slate-500'}`}>
                {i + 1}
              </span>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002249] to-[#457B9D] flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
                {m.picture ? <Img variant="avatar" src={resolveImg(m.picture)} alt="" className="w-full h-full object-cover" /> : (m.name?.charAt(0) || '?')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-[#002249] truncate">{m.name}</span>
                  {m.leadership_level && <LeadershipBadge badges={m.badges} leadershipLevel={m.leadership_level} />}
                </div>
                <p className="text-[11px] text-slate-500 truncate">{m.company || m.email}</p>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-[11px] text-slate-500">
                <span><strong className="text-[#002249]">{m.activity.posts}</strong> posts</span>
                <span><strong className="text-[#002249]">{m.activity.comments}</strong> comments</span>
                <span><strong className="text-[#002249]">{m.activity.events}</strong> events</span>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-[#002249]">{m.activity.score}</div>
                <p className="text-[10px] text-slate-400">score</p>
              </div>
              <div className={`w-2 h-2 rounded-full ${HEALTH_COLORS[m.health_status]?.bg || 'bg-slate-300'}`} title={m.health_status} />
            </div>
          ))}
        </div>
      )}

      {/* TAB: All Members */}
      {activeTab === 'members' && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="all-members-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Member</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden md:table-cell">Company</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Score</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Profile</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Health</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Posts</th>
                </tr>
              </thead>
              <tbody>
                {members && members.map((m, i) => (
                  <tr key={m.id} className="border-t border-slate-50 hover:bg-slate-50/50" data-testid={`member-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#002249] to-[#457B9D] flex items-center justify-center text-white text-[11px] font-bold overflow-hidden flex-shrink-0">
                          {m.picture ? <Img variant="avatar" src={resolveImg(m.picture)} alt="" className="w-full h-full object-cover" /> : (m.name?.charAt(0) || '?')}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="text-[13px] font-semibold text-[#002249] truncate">{m.name}</span>
                            {m.leadership_level && <LeadershipBadge badges={m.badges} leadershipLevel={m.leadership_level} />}
                          </div>
                          <p className="text-[11px] text-slate-400 truncate">{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600 truncate hidden md:table-cell">{m.company || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-sm font-bold ${m.activity.score >= 50 ? 'text-emerald-600' : m.activity.score >= 20 ? 'text-amber-600' : 'text-slate-400'}`}>{m.activity.score}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="w-10 h-1.5 bg-slate-200 rounded-full mx-auto overflow-hidden">
                        <div className={`h-full rounded-full ${m.profile_score >= 80 ? 'bg-emerald-500' : m.profile_score >= 50 ? 'bg-blue-500' : m.profile_score >= 25 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${m.profile_score}%` }} />
                      </div>
                      <span className="text-[10px] text-slate-400">{m.profile_score}%</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${HEALTH_COLORS[m.health_status]?.light || 'bg-slate-50'} ${HEALTH_COLORS[m.health_status]?.text || 'text-slate-500'}`}>
                        {HEALTH_COLORS[m.health_status]?.label || m.health_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-[12px] text-slate-600 hidden sm:table-cell">{m.activity.posts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardDashboardPage;
